import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationScoping } from './useOrganizationScoping';
import { toast } from '@/hooks/use-toast';

export interface OrganizationMember {
  id: string;
  user_id: string;
  org_id: string;
  role: 'admin' | 'member' | 'guest';
  created_at: string;
  updated_at: string;
  profiles: {
    name: string | null;
    email: string | null;
    phone_number: string | null;
    avatar_url: string | null;
  };
}

export interface InviteData {
  email: string;
  role: 'admin' | 'member' | 'guest';
  name?: string;
}

export const useOrganizationMembers = (explicitOrgId?: string) => {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const { organizationId: scopedOrgId } = useOrganizationScoping();
  
  // Use explicit org ID if provided, otherwise fall back to scoped org ID
  const organizationId = explicitOrgId || scopedOrgId;

  useEffect(() => {
    if (organizationId) {
      fetchMembers();
    }
  }, [organizationId]);

  const fetchMembers = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organization_user_roles')
        .select(`
          *,
          profiles (
            name,
            email,
            phone_number,
            avatar_url
          )
        `)
        .eq('org_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMembers((data as any[])?.filter(item => item.profiles) || []);
    } catch (error) {
      console.error('Error fetching organization members:', error);
      toast({
        title: "Error",
        description: "Failed to load organization members",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const inviteMember = async (inviteData: InviteData) => {
    if (!organizationId) return;

    try {
      setInviting(true);
      
      // First, check if user exists with this email
      const { data: existingUser, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', inviteData.email)
        .maybeSingle();

      if (userError && userError.code !== 'PGRST116') {
        throw userError;
      }

      let userId: string;

      if (existingUser) {
        // User exists, just add them to the organization
        userId = existingUser.id;
        
        // Check if they're already a member
        const { data: existingMember } = await supabase
          .from('organization_user_roles')
          .select('id')
          .eq('user_id', userId)
          .eq('org_id', organizationId)
          .maybeSingle();

        if (existingMember) {
          toast({
            title: "Error",
            description: "User is already a member of this organization",
            variant: "destructive",
          });
          return;
        }
      } else {
        // User doesn't exist, create a profile for them
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            email: inviteData.email,
            name: inviteData.name || null,
          })
          .select('id')
          .single();

        if (profileError) throw profileError;
        userId = newProfile.id;
      }

      // Add user to organization
      const { error: roleError } = await supabase
        .from('organization_user_roles')
        .insert({
          user_id: userId,
          org_id: organizationId,
          role: inviteData.role,
        });

      if (roleError) throw roleError;

      // Refresh members list
      await fetchMembers();

      toast({
        title: "Success",
        description: `Successfully invited ${inviteData.email} to the organization`,
      });
    } catch (error) {
      console.error('Error inviting member:', error);
      toast({
        title: "Error",
        description: "Failed to invite member",
        variant: "destructive",
      });
    } finally {
      setInviting(false);
    }
  };

  const removeMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('organization_user_roles')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      // Update local state
      setMembers(prev => prev.filter(m => m.id !== memberId));

      toast({
        title: "Success",
        description: "Member removed successfully",
      });
    } catch (error) {
      console.error('Error removing member:', error);
      toast({
        title: "Error",
        description: "Failed to remove member",
        variant: "destructive",
      });
    }
  };

  const updateMemberRole = async (memberId: string, newRole: 'admin' | 'member' | 'guest') => {
    try {
      const { error } = await supabase
        .from('organization_user_roles')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      // Update local state
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, role: newRole } : m
      ));

      toast({
        title: "Success",
        description: "Member role updated successfully",
      });
    } catch (error) {
      console.error('Error updating member role:', error);
      toast({
        title: "Error",
        description: "Failed to update member role",
        variant: "destructive",
      });
    }
  };

  return {
    members,
    loading,
    inviting,
    inviteMember,
    removeMember,
    updateMemberRole,
    refetch: fetchMembers,
  };
};